import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styles from './Navbar.module.css';

const links = [
  { to: '/', label: 'Home', end: true },
  {
    label: 'About Us',
    subLinks: [
      { to: '/about-us/our-team', label: 'Our Team' },
      { to: '/about-us/clubs-and-activities', label: 'Clubs and Activities' },
      { to: '/about-us/charity-and-volunteering', label: 'Charity and Volunteering' },
      { to: '/about-us/our-partners', label: 'Our Partners' },
      
    ],
  },
  { to: '/membership', label: 'Membership' },
  { to: '/events', label: 'Events' },
  { to: '/contact-us', label: 'Contact Us' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [isTop, setIsTop] = useState(true);
  const [bgReady, setBgReady] = useState(false); // defer decorative bg

  useEffect(() => {
    const handleScroll = () => setIsTop(window.scrollY === 0);
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // Defer background image until after first frame (won't block LCP)
    const id = requestAnimationFrame(() => setBgReady(true));
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(id);
    };
  }, []);

  // Pick an appropriate background size for current viewport to avoid over-fetching
  const pickSize = () => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    if (vw >= 1200) return 1600;
    if (vw >= 768) return 1200;
    return 800;
  };

  const size = pickSize();
  const navBgValue = bgReady
    ? `image-set(
        url(/optimized/nav-bg/nav-bg-${size}.avif) type('image/avif'),
        url(/optimized/nav-bg/nav-bg-${size}.webp) type('image/webp'),
        url(/optimized/nav-bg/nav-bg-${size}.jpg) type('image/jpeg')
      )`
    : 'none';

  return (
    <header
      className={`${styles.header} ${isTop ? styles.atTop : ''}`}
      style={{ '--nav-bg': navBgValue }}
    >
      <div className={`container ${styles.inner}`}>
        <NavLink to="/" className={styles.logo} onClick={() => setOpen(false)} aria-label="TWIG Home">
          {/* Optimized, responsive logo (fixes 1280px image at 54px display) */}
          <picture>
            <source
              type="image/avif"
              srcSet="/optimized/logo/logo-80.avif 80w, /optimized/logo/logo-160.avif 160w"
            />
            <source
              type="image/webp"
              srcSet="/optimized/logo/logo-80.webp 80w, /optimized/logo/logo-160.webp 160w"
            />
            <img
              src="/optimized/logo/logo-80.png"
              srcSet="/optimized/logo/logo-80.png 80w, /optimized/logo/logo-160.png 160w"
              sizes="54px"
              alt="TWIG Logo"
              width="58"
              height="54"
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
          </picture>
        </NavLink>

        <nav>
          <ul className={`${styles.menu} ${open ? styles.open : ''}`}>
            {links.map((link, index) => (
              <li key={index} className={styles.menuItem}>
                {link.subLinks ? (
                  <div className={styles.dropdown}>
                    <NavLink
                      to={link.subLinks[0].to}
                      className={({ isActive }) =>
                        isActive ? `${styles.active} ${styles.dropdownLabel}` : styles.dropdownLabel
                      }
                      onClick={() => setOpen(false)}
                    >
                      {link.label}
                    </NavLink>
                    <ul className={styles.submenu}>
                      {link.subLinks.map((sub, subIndex) => (
                        <li key={subIndex}>
                          <NavLink
                            to={sub.to}
                            className={({ isActive }) => (isActive ? styles.active : undefined)}
                            onClick={() => setOpen(false)}
                          >
                            {sub.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <NavLink
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) => (isActive ? styles.active : undefined)}
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <button
          className={styles.burger}
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
        >
          <span className={styles.line} />
          <span className={styles.line} />
          <span className={styles.line} />
        </button>
      </div>
    </header>
  );
}
